import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams, useNavigate } from "react-router-dom";
import { copilotAskUrl, ticketReplyPrompt } from "@/lib/copilotLink";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState, cacheGet } from "@/lib/swr";
import { useDash, fileUrl } from "./ctx";
import { V, useVocab } from "@/lib/vocab";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextAssist } from "@/components/TextAssist";
import { RequestsTableSettings } from "./RequestsTableSettings";
import { RequestsManageModal } from "./RequestsManageModal";
import {
  Wrench, CalendarCheck, DollarSign, ClipboardList, ShieldAlert, Clock, PlayCircle, PauseCircle,
  CheckCircle2, RotateCcw, UserCircle2, MessageSquareText, Send,
  Paperclip, Tag, CalendarDays, Phone, Reply, Bot, Headset, Download,
  ChevronRight, ChevronLeft, KeyRound, Ruler, AlertTriangle, Home, Sparkles, MapPin, Loader2, ArrowDownWideNarrow,
  CheckCheck, CalendarClock, Search, Plus, SlidersHorizontal, Settings2, ArrowUp, ArrowDown, History,
  Mail, Hash, UserPlus, Check, X, MessageSquareHeart, Archive, ArchiveRestore, Trash2, HardHat,
} from "lucide-react";

const ALL_COLUMNS = [
  { key: "title", label: "Title", sort: "title" },
  { key: "status", label: "Status", sort: "status" },
  { key: "priority", label: "Priority", sort: "priority" },
  { key: "category", label: "Category", sort: "category" },
  { key: "created_by", label: "Created by", sort: null },
  { key: "assignee", label: "Assigned to", sort: "assignee" },
  { key: "unit", label: "Unit", sort: "unit" },
  { key: "account", label: "Account #", sort: null },
  { key: "last_activity", label: "Last activity", sort: "last_activity" },
  { key: "created_date", label: "Creation date", sort: "created" },
];
const COL_META = Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, c]));
const DEFAULT_VISIBLE = ["title", "status", "priority", "category", "created_by", "assignee", "unit", "last_activity"];
const COLS_KEY = "askredline.reqCols.v1";
function loadCols() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLS_KEY) || "null");
    if (Array.isArray(saved) && saved.length) {
      const keys = saved.map((s) => s.key);
      const merged = saved.filter((s) => ALL_COLUMNS.some((c) => c.key === s.key));
      for (const c of ALL_COLUMNS) if (!keys.includes(c.key)) merged.push({ key: c.key, visible: DEFAULT_VISIBLE.includes(c.key) });
      return merged;
    }
  } catch { /* ignore */ }
  return ALL_COLUMNS.map((c) => ({ key: c.key, visible: DEFAULT_VISIBLE.includes(c.key) }));
}

const typeMetaFactory = () => ({
  maintenance: { icon: Wrench, color: "text-redline-600 bg-redline-50", label: "Maintenance" },
  rsvp: { icon: CalendarCheck, color: "text-violet-600 bg-violet-50", label: "Event RSVP" },
  dues: { icon: DollarSign, color: "text-emerald-600 bg-emerald-50", label: `${V.many("dues")}` },
  billing: { icon: DollarSign, color: "text-emerald-600 bg-emerald-50", label: "Billing" },
  amenity: { icon: Home, color: "text-sky-600 bg-sky-50", label: "Amenity" },
  access: { icon: KeyRound, color: "text-indigo-600 bg-indigo-50", label: "Access" },
  architectural: { icon: Ruler, color: "text-amber-700 bg-amber-50", label: "Architectural" },
  complaint: { icon: AlertTriangle, color: "text-orange-600 bg-orange-50", label: "Complaint" },
  security_incident: { icon: ShieldAlert, color: "text-red-700 bg-red-100", label: "Security" },
  new_resident: { icon: UserPlus, color: "text-amber-700 bg-amber-100", label: `New ${V.lc("person")}` },
  feedback: { icon: MessageSquareHeart, color: "text-pink-600 bg-pink-50", label: "Feedback" },
  address_change: { icon: Home, color: "text-teal-700 bg-teal-50", label: "Address change" },
  general: { icon: Sparkles, color: "text-ink/70 bg-sand-100", label: "General" },
});
const CATEGORIES = [
  ["maintenance", "Maintenance"], ["rsvp", "Event RSVP"], ["dues", "Dues"], ["billing", "Billing"],
  ["amenity", "Amenity"], ["access", "Access / Gate / Fob"], ["architectural", "Architectural (ARC)"],
  ["complaint", "Complaint / Violation"], ["security_incident", "Security"], ["feedback", "Feedback"],
  ["address_change", "Address change"], ["general", "General"],
];
const CAT_LABEL = Object.fromEntries(CATEGORIES);
const SEVERITY_META = { high: "bg-red-600 text-white", medium: "bg-amber-500 text-white", low: "bg-sand-300 text-ink" };
const PRIORITY_META = {
  urgent: { label: "Urgent", cls: "bg-red-600 text-white", dot: "bg-red-600", rank: 0 },
  high: { label: "High", cls: "bg-orange-500 text-white", dot: "bg-orange-500", rank: 1 },
  normal: { label: "Normal", cls: "bg-sand-200 text-ink/70", dot: "bg-sand-400", rank: 2 },
  low: { label: "Low", cls: "bg-sand-100 text-ink/50", dot: "bg-sand-300", rank: 3 },
};
const PRIORITIES = [["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]];
const prioOf = (x) => (PRIORITY_META[x.priority] ? x.priority : "normal");
const STATUS_META = {
  open: { label: "Open", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-500", icon: Clock },
  in_progress: { label: "In process", cls: "bg-blue-100 text-blue-800", dot: "bg-blue-500", icon: PlayCircle },
  // Parked on purpose — waiting on a board vote, a part, an owner, the weather. Still ours.
  on_hold: { label: "On hold", cls: "bg-violet-100 text-violet-800", dot: "bg-violet-500", icon: PauseCircle },
  resolved: { label: "Completed", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500", icon: CheckCircle2 },
  // Closed = junk / duplicate / no action needed. Replaces the old Trash (user decision):
  // nothing is ever hidden — closed tickets stay on record behind the status filter.
  closed: { label: "Closed", cls: "bg-sand-200 text-ink/60", dot: "bg-sand-300", icon: X },
};
const norm = (s) => (STATUS_META[s] ? s : "open");
const DONE = (s) => s === "resolved" || s === "closed";
const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const metaFor = (x) => { const TYPE_META = typeMetaFactory(); return TYPE_META[x.category] || TYPE_META[x.type] || TYPE_META.maintenance; };
const isSecurity = (x) => x.category === "security_incident" || x.type === "security_incident";
const isEmergency = (x) => isSecurity(x) && !DONE(norm(x.status));
const slaState = (x) => {
  const st = norm(x.status);
  if (!x.due_at || DONE(st)) return null;
  const hrs = (new Date(x.due_at).getTime() - Date.now()) / 3.6e6;
  if (hrs < 0) return { kind: "overdue", label: "Overdue", cls: "bg-red-100 text-red-700" };
  if (hrs <= 24) return { kind: "soon", label: "Due soon", cls: "bg-amber-100 text-amber-800" };
  return { kind: "ok", label: "On track", cls: "bg-emerald-50 text-emerald-700" };
};
const PAGE_SIZE = 15;

const Label = ({ icon: Icon, children }) => (
  <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-ink/50 flex items-center gap-1.5 mb-2">
    {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={2} />} {children}
  </div>
);

function QueueRow({ x, emergency, onOpen }) {
  const meta = metaFor(x);
  const st = norm(x.status); const sm = STATUS_META[st];
  const sev = x.severity && isSecurity(x) ? x.severity : null;
  return (
    <button data-testid={`action-card-${x.id}`} onClick={() => onOpen(x)}
      className={`group w-full text-left bg-white rounded-xl border p-4 flex items-center gap-4 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-redline-500 focus-visible:ring-offset-2 ${emergency ? "border-danger-300 border-l-4 border-l-danger-600 bg-danger-50/50" : "border-sand-200 hover:border-sand-300"}`}>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
        <meta.icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {x.ref && <span className="text-[10px] font-mono font-bold text-redline-600 bg-redline-50 rounded px-1.5 py-0.5 shrink-0">{x.ref}</span>}
          {(() => { const p = prioOf(x); return (p === "urgent" || p === "high") ? <span data-testid={`priority-badge-${x.id}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase shrink-0 ${PRIORITY_META[p].cls}`}>{PRIORITY_META[p].label}</span> : null; })()}
          <span className="font-heading font-bold text-ink truncate">{x.title || x.summary || "Request"}</span>
          {sev && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase shrink-0 ${SEVERITY_META[sev] || SEVERITY_META.low}`}>{sev}</span>}
          {(() => { const s = slaState(x); return s && s.kind !== "ok" ? <span data-testid={`sla-badge-${x.id}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase shrink-0 ${s.cls}`}>{s.label}</span> : null; })()}
        </div>
        <div className="text-sm text-ink/60 line-clamp-1">{x.details || CAT_LABEL[x.category] || meta.label}</div>
        <div className="text-xs text-ink/50 mt-1 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-ink/70">{x.resident || "Unknown"}</span>
          {homeLabel(x) && <span>· {homeLabel(x)}</span>}
          <span className="text-ink/30">·</span>
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmt(x.created_at)}</span>
        </div>
      </div>
      <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0 w-32">
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${sm.cls}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${sm.dot}`} /> {sm.label}
        </span>
        {x.assignee_name
          ? <span className="text-xs text-ink/60 inline-flex items-center gap-1 truncate max-w-full"><UserCircle2 className="h-3.5 w-3.5 shrink-0" /> {x.assignee_name}</span>
          : <span className="text-xs text-ink/30">Unassigned</span>}
      </div>
      <ChevronRight className="h-5 w-5 text-ink/30 group-hover:text-redline-500 transition-colors shrink-0" />
    </button>
  );
}

// ─── field services: send this request out to the company that does the work ──
const FS_CATS = [["common_area_cleaning", "Common-area cleaning"], ["trash", "Trash & garbage"],
  ["landscaping", "Landscaping"], ["plumbing", "Plumbing"], ["electrical", "Electrical"],
  ["hvac", "HVAC"], ["pest_control", "Pest control"], ["pool", "Pool"], ["handyman", "Handyman"],
  ["janitorial", "Janitorial"], ["other", "Other"]];
const FS_JOB_LABEL = {
  reported: "Logged, not sent yet", dispatched: "Sent to the crew", scheduled: "Accepted",
  enroute: "On the way", no_response: "Nobody has answered", overdue: "Accepted · overdue",
  completed: "Finished", completed_unverified: "Finished · no photo", closed: "Closed",
  cancelled: "Cancelled",
};

function JobCard({ tenantId, action, moduleLabel, readOnly, onSent }) {
  const [cat, setCat] = useState("handyman");
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(null);
  const wo = action.wo;

  // Is this already riding along on a company's next contracted visit? A manager chasing a
  // "nothing has happened" complaint needs to see that before they dispatch it twice.
  useEffect(() => {
    if (wo) return;
    let live = true;
    api.get(`/tenants/${tenantId}/field-services/requests-queue-map`)
      .then(({ data }) => { if (live) setQueued((data.map || {})[action.id] || false); })
      .catch(() => setQueued(false));
    return () => { live = false; };
  }, [tenantId, action.id, wo]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/work-orders/from-ticket/${action.id}`, { category: cat });
      toast.success(data.blocked ? "Logged — but nobody could be dispatched yet"
        : data.routed_via === "direct" ? "Sent straight to the crew on site" : "Sent out to the vendor");
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send that out"); }
    setBusy(false);
  };

  const queue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/queue`,
        { action_id: action.id, category: cat });
      toast.success(`On ${data.item?.vendor_name || "their"} next visit${data.due ? ` (${data.due})` : ""} — the ${V.lc("person")} was told`);
      onSent();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : det?.message || "Couldn't add it to a visit");
    }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="ticket-job-card">
      <Label icon={HardHat}>{moduleLabel}</Label>
      {wo ? (
        <div className="space-y-2">
          <div className="text-sm font-bold text-ink">{FS_JOB_LABEL[wo.status] || wo.status}</div>
          <div className="text-xs text-ink/60">
            {wo.vendor_name || "No vendor yet"}{wo.tech_name ? ` · ${wo.tech_name}` : ""}
          </div>
          {wo.location && <div className="text-xs text-ink/50">{wo.location}</div>}
          <a data-testid="ticket-job-open" href="/dashboard/field-services?tab=spine"
            className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-redline-600 hover:underline">
            Open in {moduleLabel} <ChevronRight className="h-3.5 w-3.5" />
          </a>
        </div>
      ) : queued ? (
        <div className="space-y-2" data-testid="ticket-queued">
          <div className="text-sm font-bold text-ink">
            {queued.status === "pending_confirm" ? "Waiting on your OK"
              : `On ${queued.vendor_name}'s ${queued.day || "next"} visit`}
          </div>
          <p className="text-xs leading-relaxed text-ink/55">
            {queued.status === "pending_confirm"
              ? `This is marked high, so we're waiting for you to confirm it goes on ${queued.vendor_name}'s visit rather than sending someone out now.`
              : `The ${V.lc("person")} has been told it's on the list, and they'll be texted again when it's done.`}
            {(queued.rolled_count || 0) > 0 && ` It has rolled over ${queued.rolled_count} time(s).`}
          </p>
          <a data-testid="ticket-queue-open" href="/dashboard/field-services?tab=queue"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-redline-600 hover:underline">
            Open the visit queue <ChevronRight className="h-3.5 w-3.5" />
          </a>
        </div>
      ) : readOnly ? (
        <p className="text-xs text-ink/50">Not out with a vendor.</p>
      ) : ["resolved", "closed"].includes(norm(action.status)) || action.archived ? (
        <p className="text-xs text-ink/50" data-testid="ticket-job-put-away">
          This request has been put away. Reopen it to send it out to a vendor.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs leading-relaxed text-ink/55">
            Send this to the company that does this work. The crew on site gets it on their phone
            {action.source === "board" ? "." : `, and the ${V.lc("person")} can follow it in their portal.`}
          </p>
          <select data-testid="ticket-job-category" value={cat} onChange={(e) => setCat(e.target.value)}
            className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
            {FS_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="ticket-job-send" onClick={send} disabled={busy}
            className="w-full rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink/90 disabled:opacity-50 flex items-center justify-center gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardHat className="h-4 w-4" />} Send to a vendor
          </button>
          <button data-testid="ticket-job-queue" onClick={queue} disabled={busy}
            className="w-full rounded-full border border-sand-300 text-ink/70 text-sm font-semibold px-4 py-2 hover:border-ink/30 disabled:opacity-50 flex items-center justify-center gap-1.5">
            <CalendarClock className="h-4 w-4" /> Add to their next visit
          </button>
          <p className="text-[11px] leading-relaxed text-ink/40">
            If a company already comes here on a schedule for this trade, this rides along on their next
            visit instead of pulling a crew out for one bag of trash.
          </p>
        </div>
      )}
    </div>
  );
}

export default function Actions() {
  const TYPE_META = typeMetaFactory();
  const { tenantId, tenant, role, me, setTenantId } = useDash();
  const navigate = useNavigate();
  const v = useVocab();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("");
  const [sortField, setSortField] = useState("last_activity");
  const [sortDir, setSortDir] = useState("desc");
  // Cache key covers every query param so each view hydrates instantly with ITS last-known rows.
  const reqKey = `actions:${tenantId}:${page}:${pageSize}:${q}:${statusFilter}:${categoryFilter}:${mineOnly}:${sourceFilter}:${sortField}:${sortDir}`;
  const [items, setItems] = useCachedState(`${reqKey}:items`, []);
  const [total, setTotal] = useCachedState(`${reqKey}:total`, 0);
  const [loading, setLoading] = useState(() => cacheGet(`${reqKey}:items`) === undefined);
  const [columns, setColumns] = useState(loadCols);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [customCats, setCustomCats] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [fullThread, setFullThread] = useState(false); // ticket panel: focused excerpt vs whole thread
  const [admins, setAdmins] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentVis, setCommentVis] = useState("public"); // public → resident sees + notified; internal → staff-only
  const [postingComment, setPostingComment] = useState(false);
  const commentsEndRef = useRef(null);
  useEffect(() => {
    if (detail?.comments?.length) commentsEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [detail?.comments?.length]);
  const [reply, setReply] = useState("");
  const [notifyDraft, setNotifyDraft] = useState(null);
  const [teach, setTeach] = useState(null); // {actionId, title, answer, loading, empty}
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [households, setHouseholds] = useState([]);
  const [nf, setNf] = useState(null);
  const [nfFiles, setNfFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null); // full-size image viewer for request photos
  const fileRef = useRef(null);
  const upFileRef = useRef(null);
  const nfFileRef = useRef(null);
  const readOnly = role === "board_member";
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    api.get(`/tenants/${tenantId}/actions`, { params: {
      page, page_size: pageSize, q, status: statusFilter,
      category: categoryFilter, assignee_id: mineOnly && me ? me : "", source: sourceFilter, sort: sortField, dir: sortDir,
    } }).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [tenantId, page, pageSize, q, statusFilter, categoryFilter, mineOnly, me, sourceFilter, sortField, sortDir]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => setQ(qInput.trim()), 350); return () => clearTimeout(t); }, [qInput]);
  const loadCats = useCallback(() => { if (tenantId) api.get(`/tenants/${tenantId}/request-categories`).then((r) => setCustomCats(r.data)).catch(() => {}); }, [tenantId]);
  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { try { localStorage.setItem(COLS_KEY, JSON.stringify(columns)); } catch { /* ignore */ } }, [columns]);
  useEffect(() => { if (tenantId) api.get(`/tenants/${tenantId}/assignees`).then((r) => setAdmins(r.data)).catch(() => setAdmins([])); }, [tenantId]);
  // Who a board request can be addressed to — this community's HOA managers only.
  const [managers, setManagers] = useState([]);
  useEffect(() => { if (tenantId) api.get(`/tenants/${tenantId}/community-managers`).then((r) => setManagers(r.data.managers || [])).catch(() => setManagers([])); }, [tenantId]);
  useEffect(() => { if (tenantId) api.get(`/tenants/${tenantId}/households`).then((r) => setHouseholds(r.data)).catch(() => {}); }, [tenantId]);
  const residentOptions = households.flatMap((h) => (h.people || []).map((p) => ({ hid: h.id, pid: p.id, name: p.name, label: `${p.name || `${V.one("person")}`}${homeLabel(h) ? ` · ${homeLabel(h)}` : ""}${p.phone ? ` · ${p.phone}` : ""}` })));
  const openNew = () => { setNfFiles([]); setNf({ title: "", category: "maintenance", priority: "normal", details: "", severity: "", household_id: "", person_id: "", resident: "", due_at: "", board: false }); setNewOpen(true); };
  const openBoardRequest = () => { setNfFiles([]); setNf({ title: "", category: "general", priority: "normal", details: "", severity: "", household_id: "", person_id: "", resident: "", due_at: "", board: true, assignee_id: (managers.find((m) => m.is_primary) || managers[0])?.user_id || "" }); setNewOpen(true); };
  const createRequest = async () => {
    if (!nf.title.trim()) { toast.error("Add a short title"); return; }
    if (nf.board && !nf.assignee_id) { toast.error("Choose which manager this goes to"); return; }
    setBusy(true);
    try {
      const url = nf.board ? `/tenants/${tenantId}/board-requests` : `/tenants/${tenantId}/actions`;
      const r = await api.post(url, nf);
      for (const f of nfFiles) {
        const fd = new FormData(); fd.append("file", f);
        try { await api.post(`/actions/${r.data.id}/upload`, fd); }
        catch (err) { toast.error(`${f.name}: ${err.response?.data?.detail || "couldn't be attached"}`); }
      }
      toast.success(nf.board ? `Request ${r.data.ref} sent to the management team` : `Request ${r.data.ref} created`);
      setNewOpen(false); load(); openTicket(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create request"); }
    finally { setBusy(false); }
  };
  useEffect(() => { setPage(1); }, [q, statusFilter, categoryFilter, mineOnly, sourceFilter, pageSize, sortField, sortDir]);

  // Bulk close/reopen from the queue: checkbox selection + one-click status change with Undo.
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [tenantId, page, q, statusFilter, categoryFilter, mineOnly, sourceFilter, pageSize]);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((x) => selected.has(x.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((x) => x.id)));
  const bulkSetStatus = async (toStatus) => {
    const ids = [...selected];
    if (!ids.length) return;
    // Remember each ticket's current status so Undo can replay it exactly.
    const prev = items.filter((x) => ids.includes(x.id)).map((x) => ({ id: x.id, status: norm(x.status) }));
    try {
      await api.post("/actions/bulk-status", { ids, status: toStatus });
      const verb = toStatus === "closed" ? "Closed" : "Reopened";
      toast.success(`${verb} ${ids.length} ticket${ids.length === 1 ? "" : "s"}`, {
        action: { label: "Undo", onClick: async () => {
          try { await api.post("/actions/bulk-status", { items: prev }); toast.success("Undone"); load(); }
          catch { toast.error("Could not undo"); }
        } },
      });
      setSelected(new Set()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update tickets"); }
  };

  // Archive = out of every list and count, still on record. Delete = permanently gone
  // after a short undo window. Both work on one ticket or a whole selection.
  const archiveIds = async (ids, archived) => {
    if (!ids.length) return;
    try {
      await api.post("/actions/bulk-archive", { ids, archived });
      const refresh = () => { if (openId && ids.includes(openId)) refreshDetail(openId); else load(); };
      toast.success(archived
        ? `Archived ${ids.length} request${ids.length === 1 ? "" : "s"}`
        : `Restored ${ids.length} request${ids.length === 1 ? "" : "s"} from the archive`, {
        action: { label: "Undo", onClick: async () => {
          try { await api.post("/actions/bulk-archive", { ids, archived: !archived }); toast.success("Undone"); refresh(); }
          catch { toast.error("Could not undo"); }
        } },
      });
      setSelected(new Set()); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not archive"); }
  };
  const deleteIds = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} request${ids.length === 1 ? "" : "s"} permanently? You'll have a moment to undo, then it's gone for good.`)) return;
    try {
      await api.post("/actions/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length} request${ids.length === 1 ? "" : "s"}`, {
        duration: 10000,
        action: { label: "Undo", onClick: async () => {
          try { await api.post("/actions/bulk-restore", { ids }); toast.success("Restored"); load(); }
          catch { toast.error("Could not restore"); }
        } },
      });
      setSelected(new Set()); closeTicket(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const bulkArchive = (archived) => archiveIds([...selected], archived);
  const bulkDelete = () => deleteIds([...selected]);
  const canDelete = !readOnly && ["super_admin", "platform_admin", "company_admin", "regional_manager", "hoa_manager"].includes(role);

  const openTicket = async (a) => {
    setOpenId(a.id); setDetail(null); setReply(""); setNotifyDraft(null); setFullThread(false);
    if (searchParams.get("ticket") !== a.id) { const p = new URLSearchParams(searchParams); p.set("ticket", a.id); setSearchParams(p); }
    window.scrollTo({ top: 0 });
    try {
      const r = await api.get(`/actions/${a.id}`);
      setDetail(r.data);
    } catch (e) {
      // A deep link (from an email or the notification bell) can point at a request that was
      // archived, deleted, or belongs to another community. Say so instead of spinning forever.
      const code = e?.response?.status;
      toast.error(code === 404 || code === 403
        ? "That request isn't here any more — it may have been deleted or archived."
        : "Couldn't open that request. Try again in a moment.");
      closeTicket();
    }
  };
  const closeTicket = () => {
    setOpenId(null); setDetail(null);
    if (searchParams.get("ticket")) { const p = new URLSearchParams(searchParams); p.delete("ticket"); setSearchParams(p, { replace: true }); }
  };

  // URL-driven ticket view: ?ticket=<id> opens the full-page ticket (deep links from
  // notifications, browser back/forward closes/reopens naturally).
  useEffect(() => {
    const tid = searchParams.get("ticket");
    if (tid && tid !== openId) openTicket({ id: tid });
    else if (!tid && openId) { setOpenId(null); setDetail(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const refreshDetail = async (id) => { const r = await api.get(`/actions/${id}?full=${fullThread ? 1 : 0}`); setDetail(r.data); load(); };
  const toggleThread = async (id, full) => {
    setFullThread(full);
    const r = await api.get(`/actions/${id}?full=${full ? 1 : 0}`);
    setDetail(r.data);
  };
  const patch = async (id, body) => { await api.put(`/actions/${id}`, body); refreshDetail(id); };

  const setStatus = async (id, status) => {
    await api.put(`/actions/${id}`, { status });
    refreshDetail(id);
    if (!noResident && (status === "in_progress" || status === "resolved")) {
      const first = (detail?.action?.resident || "there").split(" ")[0];
      const msg = status === "resolved"
        ? `Hi ${first}, good news — your request is complete. Thanks for letting us know! Reply here if anything's still not right.`
        : `Hi ${first}, quick update — we're now working on your request. We'll let you know as soon as it's done.`;
      setNotifyDraft({ text: msg });
    } else setNotifyDraft(null);
    toast.success(`Marked ${STATUS_META[status].label}`);
  };
  const assign = async (id, u) => { await patch(id, { assignee_id: u?.user_id || "", assignee_name: u?.name || "" }); toast.success(u ? `Assigned to ${u.name}` : "Unassigned"); };
  const setDue = async (id, val) => { await patch(id, { due_at: val ? new Date(val + "T17:00:00").toISOString() : null }); toast.success(val ? "Due date set" : "Due date cleared"); };
  const saveField = async (id, field, value) => { await patch(id, { [field]: value }); };
  const addComment = async (id) => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await api.post(`/actions/${id}/comments`, { text: commentText.trim(), visibility: commentVis });
      setCommentText(""); refreshDetail(id);
      toast.success(commentVis === "public" ? `Reply sent to ${V.lc("person")}` : "Internal comment added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post that"); }
    finally { setPostingComment(false); }
  };

  const openTeach = async (id) => {
    setTeach({ actionId: id, title: "", answer: "", loading: true, empty: false });
    try {
      const r = await api.post(`/actions/${id}/draft-knowledge`);
      const d = r.data.draft || {};
      const empty = !(d.title || "").trim() && !(d.answer || "").trim();
      setTeach({ actionId: id, title: d.title || "", answer: d.answer || "", loading: false, empty });
    } catch (e) {
      toast.error("Could not draft knowledge");
      setTeach(null);
    }
  };
  const saveTeach = async () => {
    if (!teach?.title.trim() || !teach?.answer.trim()) { toast.error("Add a question and answer"); return; }
    setBusy(true);
    try {
      await api.post(`/actions/${teach.actionId}/teach-knowledge`, { title: teach.title, answer: teach.answer });
      toast.success("Added to your AI's knowledge");
      const id = teach.actionId; setTeach(null); refreshDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const sendReply = async (id, text, clear) => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/actions/${id}/reply`, { text }); if (clear) clear();
      toast.success(r.data.delivered ? `Sent to ${V.lc("person")}` : "Saved (SMS not configured)");
      // A focused excerpt with a later-ticket boundary would hide the reply just sent —
      // widen to the full thread so the manager sees their message land.
      if (detail?.messages_window?.end && !fullThread) toggleThread(id, true);
      else refreshDetail(id);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };
  const onAttach = async (id, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("send", "true");
    try { const r = await api.post(`/actions/${id}/attach`, fd); toast.success(r.data.delivered ? `Document sent to ${V.lc("person")}` : "Attached"); refreshDetail(id); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  // Board raiser adding evidence to their own request after it was sent — files it on the
  // request, never texts anyone.
  const onUpload = async (id, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} is over 5 MB`); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/actions/${id}/upload`, fd); toast.success("Added to this request"); refreshDetail(id); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (upFileRef.current) upFileRef.current.value = ""; }
  };
  const closeRequest = async (id) => {
    const prev = status; // ticket's status before closing — Undo replays it exactly
    await api.put(`/actions/${id}`, { status: "closed" });
    closeTicket(); load();
    // One-tap Undo right in the toast — closed tickets stay visible under the Closed filter.
    toast.success("Request closed", {
      action: { label: "Undo", onClick: async () => {
        try { await api.put(`/actions/${id}`, { status: prev }); toast.success("Reopened"); load(); }
        catch { toast.error("Could not reopen"); }
      } },
    });
  };
  const reopenRequest = async (id) => { await api.put(`/actions/${id}`, { status: "open" }); toast.success("Request reopened"); refreshDetail(id); };

  /* New-resident approval tickets: decide right here — the roster endpoints resolve the
     ticket themselves, so approving from either place keeps both views in sync. */
  const decide = async (act, approve) => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/people/${act.approval_person_id}/${approve ? "approve" : "reject"}`);
      const at = r.data?.attached_to;
      const where = at ? ` — attached to ${homeLabel(at) || "an existing property"}` : "";
      const sms = r.data?.texted ? " · they've been texted" : ` · NOT texted (${r.data?.text_note || "unknown reason"})`;
      const line = approve
        ? `${(act.resident || "Resident").split(" ")[0]} approved${where}${sms}`
        : `Claim declined${sms}`;
      r.data?.texted ? toast.success(line) : toast.warning(line);
      window.dispatchEvent(new Event("askredline:approvals-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update — it may already be handled");
    } finally {
      setBusy(false);
      refreshDetail(act.id);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visibleCols = columns.filter((c) => c.visible);
  const allCategories = useMemo(() => {
    // Dedupe by code so a custom category that collides with a built-in (or with another
    // custom row that shares a code) can't produce duplicate <option> React keys.
    const seen = new Set(CATEGORIES.map(([v]) => v));
    const merged = [...CATEGORIES];
    for (const c of customCats) {
      if (c?.code && !seen.has(c.code)) { seen.add(c.code); merged.push([c.code, c.label]); }
    }
    return merged;
  }, [customCats]);
  const catLabel = (code) => CAT_LABEL[code] || TYPE_META[code]?.label || customCats.find((c) => c.code === code)?.label || (code ? code : "General");
  const toggleSort = (field) => {
    if (!field) return;
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
  };
  const createdByOf = (x) => x.created_by || x.resident || "Ask Redline";
  const renderCell = (key, x) => {
    const st = norm(x.status); const sm = STATUS_META[st];
    if (key === "title") {
      const p = prioOf(x); const flag = isEmergency(x) || p === "urgent" || p === "high";
      return (
        <div className="flex items-center gap-2 min-w-0">
          {flag && <span title="Urgent" className="h-2 w-2 rounded-full bg-danger-500 shrink-0" />}
          <span className="text-[11px] font-bold text-ink/40 tabular-nums shrink-0">{x.ref}</span>
          <span className="font-semibold text-ink truncate">{x.title || x.summary || "Request"}</span>
          {x.source === "board" && <span data-testid={`board-badge-${x.id}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase shrink-0 bg-indigo-100 text-indigo-700">{V.Grp()}</span>}
        </div>
      );
    }
    if (key === "status") return <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${sm.cls}`}><span className={`h-1.5 w-1.5 rounded-full ${sm.dot}`} /> {sm.label}</span>;
    if (key === "priority") { const p = prioOf(x); const pm = PRIORITY_META[p] || PRIORITY_META.normal; return <span data-testid={`priority-cell-${x.id}`} className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${pm.cls}`}>{pm.label}</span>; }
    if (key === "category") return <span className="text-ink/70">{catLabel(x.category || x.type)}</span>;
    if (key === "created_by") return <span className="text-ink/70 truncate">{createdByOf(x)}</span>;
    if (key === "assignee") return x.assignee_name ? <span className="text-ink/80">{x.assignee_name}</span> : <span className="text-ink/35">Unassigned</span>;
    if (key === "unit") return <span className="text-ink/70">{x.unit || "—"}</span>;
    if (key === "account") return x.account_number ? <span className="text-ink/70 font-mono text-xs">{x.account_number}</span> : <span className="text-ink/35">—</span>;
    if (key === "last_activity") return <span className="text-ink/60 whitespace-nowrap">{fmt(x.updated_at || x.created_at)}</span>;
    if (key === "created_date") return <span className="text-ink/60 whitespace-nowrap">{fmt(x.created_at)}</span>;
    return null;
  };

  const a = detail?.action;
  const status = a ? norm(a.status) : "open";
  const isClosed = status === "closed";
  // Internal request (a board→manager ask, or a manual ticket with nobody attached): there is
  // no resident to text, reply to publicly, or show a thread for.
  const noResident = !!a && !a.person_id && !a.household_id && !a.phone;
  useEffect(() => { if (noResident) setCommentVis("internal"); }, [noResident, openId]);

  return (
    <div className="max-w-[1200px] mx-auto" onClick={() => settingsOpen && setSettingsOpen(false)}>
      {openId ? (
        /* ============ FULL-PAGE TICKET WORKSPACE ============ */
        !a ? (
          <div className="flex items-center justify-center py-24" data-testid="ticket-page-loading"><div className="h-6 w-6 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        ) : (
          <div data-testid="ticket-page" className="pb-10">
            {/* breadcrumb / back */}
            <div className="flex items-center gap-1.5 text-sm mb-4">
              <button data-testid="ticket-back" onClick={closeTicket}
                className="inline-flex items-center gap-1 font-semibold text-ink/60 hover:text-ink transition-colors rounded-full border border-sand-200 bg-white pl-2.5 pr-3.5 py-1.5">
                <ChevronLeft className="h-4 w-4" /> All requests
              </button>
              <span className="text-ink/30 px-1">/</span>
              <span className="font-mono font-bold text-redline-600 text-xs bg-redline-50 rounded px-2 py-1">{a.external_ref || a.ref}</span>
            </div>

            {/* header card */}
            <div className="bg-white rounded-2xl border border-sand-200 px-6 pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${metaFor(a).color}`}>
                  {(() => { const M = metaFor(a).icon; return <M className="h-5 w-5" strokeWidth={1.75} />; })()}
                </div>
                <div className="flex-1 min-w-0">
                  {readOnly
                    ? <div className="font-heading text-2xl font-extrabold text-ink">{a.title || a.summary || "Request"}</div>
                    : <input data-testid="ticket-title" defaultValue={a.title || a.summary || ""} onBlur={(e) => e.target.value !== (a.title || a.summary) && saveField(a.id, "title", e.target.value)}
                        className="w-full bg-transparent outline-none border-b border-transparent hover:border-sand-200 focus:border-redline-400 text-2xl font-heading font-extrabold text-ink" />}
                  <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full inline-flex items-center gap-1.5 font-semibold ${STATUS_META[status].cls}`}><span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[status].dot}`} /> {STATUS_META[status].label}</span>
                    <span className="text-ink/50 inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {fmt(a.created_at)}</span>
                    {a.source === "board" && <span data-testid="ticket-board-badge" className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold inline-flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> {V.Grp()} request{a.raised_by_name ? ` · ${a.raised_by_name}` : ""}</span>}
                    {(() => { const s = slaState(a); return s ? <span data-testid="ticket-sla" className={`px-2 py-0.5 rounded-full font-semibold ${s.cls}`}>{s.label}{a.due_at ? ` · due ${fmt(a.due_at)}` : ""}</span> : null; })()}
                    {a.acknowledged_at && <span data-testid="ticket-ack-info" className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Ack by {a.acknowledged_by} · {fmt(a.acknowledged_at)}</span>}
                  </div>
                </div>
              </div>
            </div>

            {a.approval_person_id && !DONE(status) && (
              <div data-testid="ticket-approval-box" className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 mt-4">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <UserPlus className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-ink">Residency approval needed</div>
                    <p className="text-xs text-ink/60 mt-0.5">
                      {a.resident || "Someone"} self-registered from {a.phone || "an unknown number"}
                      {a.unit ? ` claiming unit ${a.unit}` : a.address ? ` claiming ${a.address}` : ""}.
                      Approving confirms their residency, unlocks the full concierge and texts them the good news.
                      Declining removes the claim and asks them to contact the office.
                    </p>
                    {!readOnly && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <button data-testid="ticket-approve-btn" disabled={busy} onClick={() => decide(a, true)}
                          className="h-9 rounded-full bg-emerald-600 text-white text-sm font-bold px-5 shadow-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2">
                          <Check className="h-4 w-4" strokeWidth={3} /> Approve {V.lc("person")}
                        </button>
                        <button data-testid="ticket-decline-btn" disabled={busy} onClick={() => decide(a, false)}
                          className="h-9 rounded-full bg-white border border-red-300 text-red-700 text-sm font-bold px-5 hover:bg-red-50 disabled:opacity-50 transition-colors inline-flex items-center gap-2">
                          <X className="h-4 w-4" strokeWidth={3} /> Decline claim
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {notifyDraft && !readOnly && (
              <div data-testid="ticket-notify-box" className="rounded-xl border border-blue-200 bg-blue-50/70 p-3 mt-4">
                <div className="text-xs font-semibold text-blue-800 mb-1.5 flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Text this update to the {V.lc("person")}?</div>
                <textarea value={notifyDraft.text} onChange={(e) => setNotifyDraft({ text: e.target.value })} rows={2}
                  className="w-full rounded-lg border border-blue-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 bg-white" />
                <div className="flex gap-2 mt-2">
                  <button data-testid="ticket-notify-send" disabled={busy} onClick={() => sendReply(a.id, notifyDraft.text, () => setNotifyDraft(null))} className="rounded-lg bg-blue-600 text-white text-sm font-semibold px-4 py-2 hover:bg-blue-700 disabled:opacity-50">Send update</button>
                  <button onClick={() => setNotifyDraft(null)} className="rounded-lg bg-white border border-sand-200 text-sm px-4 py-2 hover:bg-sand-50">Skip</button>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6 mt-6 items-start">
              {/* LEFT — description, conversation + composer, internal notes */}
              <div className="lg:col-span-2 space-y-6 min-w-0">
                <div className="bg-white rounded-2xl border border-sand-200 p-5">
                  <Label icon={ClipboardList}>Description</Label>
                  {readOnly ? <p className="text-sm text-ink-800 whitespace-pre-wrap">{a.details}</p>
                    : <textarea data-testid="ticket-description" defaultValue={a.details || ""} onBlur={(e) => e.target.value !== (a.details || "") && saveField(a.id, "details", e.target.value)}
                        rows={3} className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />}
                  {(a.photos || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-3">
                      {a.photos.map((p) => <img key={p} src={fileUrl(p)} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} onClick={() => setLightbox(fileUrl(p))} data-testid="ticket-photo" className="h-24 w-24 rounded-lg object-cover border border-sand-200 cursor-zoom-in hover:opacity-90 transition-opacity" />)}
                    </div>
                  )}
                  {a.photo_analysis && (
                    <div data-testid="ticket-photo-analysis" className="mt-3 flex items-start gap-2 rounded-lg bg-redline-50 border border-redline-100 px-3 py-2">
                      <Sparkles className="h-4 w-4 text-redline-500 shrink-0 mt-0.5" strokeWidth={2} />
                      <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-redline-700">AI read of the photo</div>
                        <p className="text-sm text-ink-800 whitespace-pre-wrap">{a.photo_analysis}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* conversation + composer — only when there's actually a resident behind it */}
                {!noResident && (
                <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
                  <div className="p-5 pb-3"><Label icon={MessageSquareText}>Conversation with the {V.lc("person")}</Label>
                    {detail.messages_window?.focused && ((detail.messages_window.hidden_before || 0) + (detail.messages_window.hidden_after || 0)) > 0 && (
                      <button data-testid="ticket-thread-expand" onClick={() => toggleThread(a.id, true)}
                        className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-sand-50 px-3 py-1 text-xs font-semibold text-ink/70 hover:bg-sand-100 hover:text-ink transition-colors">
                        Showing this request only · view full conversation ({(detail.messages_window.hidden_before || 0) + (detail.messages_window.hidden_after || 0)} more)
                      </button>
                    )}
                    {fullThread && (
                      <button data-testid="ticket-thread-focus" onClick={() => toggleThread(a.id, false)}
                        className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-redline-200 bg-redline-50 px-3 py-1 text-xs font-semibold text-redline-600 hover:bg-redline-100 transition-colors">
                        Full conversation · back to this request only
                      </button>
                    )}
                    <div className="space-y-2.5 max-h-[52vh] overflow-y-auto pr-1" data-testid="ticket-conversation">
                      {(detail.messages || []).length === 0 && <p className="text-xs text-ink/50">No linked conversation.</p>}
                      {(() => {
                        const msgs = detail.messages || [];
                        const createdIdx = msgs.findIndex((m) => (m.created_at || "") > (a.created_at || ""));
                        const divider = (
                          <div key="created-divider" data-testid="ticket-created-divider" className="flex items-center gap-2 py-1">
                            <div className="h-px flex-1 bg-redline-300/60" />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-redline-500">This request created · {fmt(a.created_at)}</span>
                            <div className="h-px flex-1 bg-redline-300/60" />
                          </div>
                        );
                        const out = msgs.map((m, i) => {
                        const inbound = m.direction === "inbound";
                        const who = inbound ? (a.resident || "Resident") : (m.is_human ? (m.sent_by || "Staff") : "AI concierge");
                        return (
                          <div key={i} className={`flex flex-col ${inbound ? "items-start" : "items-end"}`}>
                            <div className="text-[10px] text-ink/50 mb-0.5 flex items-center gap-1">
                              {!inbound && (m.is_human ? <Headset className="h-3 w-3" /> : <Bot className="h-3 w-3" />)} {who} · {fmt(m.created_at)}
                            </div>
                            <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm ${inbound ? "bg-sand-100 text-ink" : m.is_human ? "bg-redline-500 text-white" : "bg-ink text-white"}`}>
                              {m.body}
                              {(m.media_items && m.media_items.length ? m.media_items : (m.media || []).map((p) => ({ path: p, kind: "file" }))).map((it) => (
                                it.kind === "audio"
                                  ? <audio key={it.path} controls src={fileUrl(it.path)} data-testid={`voicemail-audio-${m.id || i}`} className="mt-1.5 w-full max-w-[260px] h-9" />
                                  : it.kind === "image"
                                    ? <img key={it.path} src={fileUrl(it.path)} alt="" onClick={() => setLightbox(fileUrl(it.path))} className="mt-1.5 rounded-lg max-h-40 object-cover cursor-zoom-in hover:opacity-90 transition-opacity" />
                                    : <a key={it.path} href={fileUrl(it.path)} target="_blank" rel="noreferrer" className={`block mt-1 text-xs underline ${inbound ? "text-redline-600" : "text-white/90"}`}>{it.filename || "View attachment"}</a>
                              ))}
                            </div>
                          </div>
                        );
                        });
                        if (msgs.length) {
                          if (createdIdx === -1) out.push(divider);
                          else out.splice(createdIdx, 0, divider);
                        }
                        return out;
                      })()}
                    </div>
                  </div>
                  {/* composer lives right under the thread, TownSq-style */}
                  {!readOnly && !isClosed && detail.reply_guard?.guard && (
                    <div data-testid="ticket-reply-guard" className="border-t border-amber-200 p-4 bg-amber-50/40">
                      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
                        <div className="flex items-start gap-2.5">
                          <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-amber-900">Can't text this caller</div>
                            <p className="text-sm text-amber-800 mt-0.5">{detail.reply_guard.message}</p>
                          </div>
                        </div>
                        {(detail.reply_guard.phone || a.phone) && (
                          <a data-testid="ticket-callback-btn" href={`tel:${detail.reply_guard.phone || a.phone}`}
                            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 transition-colors">
                            <Phone className="h-4 w-4" /> Call back {detail.reply_guard.phone || a.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {!readOnly && !isClosed && !detail.reply_guard?.guard && (
                    <div className="border-t border-sand-200 p-4 bg-sand-50/50">
                      <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-ink">
                        <MessageSquareText className="h-4 w-4 text-redline-500" /> Reply to {V.lc("person")}
                        <span className="text-xs font-normal text-ink/50">
                          {a.conversation_id ? `· sends a text to ${a.resident ? a.resident.split(" ")[0] : "their phone"}${a.phone ? ` (${a.phone})` : ""}` : `· link ${V.a("person")} to enable replies`}
                        </span>
                      </div>
                      <div className="rounded-2xl border border-sand-200 focus-within:ring-2 focus-within:ring-redline-500/30 overflow-hidden bg-white">
                        <textarea data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2} disabled={!a.conversation_id}
                          placeholder={a.conversation_id ? `Type a message that goes to the ${V.lc("person")}'s phone…` : `This request has no ${V.lc("person")} conversation to reply to.`}
                          className="w-full px-4 py-3 text-sm outline-none resize-none disabled:bg-sand-50 disabled:text-ink/40" />
                        <div className="flex items-center justify-between px-3 py-2 border-t border-sand-100 bg-sand-50/60">
                          <div className="flex items-center gap-1">
                            <input ref={fileRef} type="file" data-testid="ticket-attach-input" className="hidden" onChange={(e) => onAttach(a.id, e.target.files?.[0])} />
                            <button data-testid="ticket-attach-btn" disabled={busy} onClick={() => fileRef.current?.click()} title={`Send a document to the ${V.lc("person")}`}
                              className="h-9 px-3 rounded-lg text-ink/60 hover:bg-sand-200 hover:text-ink text-sm font-semibold flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Document</button>
                            {tenant?.copilot_enabled && (
                              <button data-testid="ticket-copilot-draft" disabled={busy} onClick={() => navigate(copilotAskUrl(ticketReplyPrompt(a, detail)))} title="Have Copilot draft this reply from the request and our rules"
                                className="h-9 px-3 rounded-lg text-ink/60 hover:bg-sand-200 hover:text-ink text-sm font-semibold flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-redline-500" /> Draft with Copilot</button>
                            )}
                            <span className="h-9 px-2 rounded-lg hover:bg-sand-200 flex items-center"><TextAssist value={reply} onChange={setReply} surface="resident_message" disabled={busy} testId="ticket-reply-assist" micTitle="Dictate reply" align="left" /></span>
                          </div>
                          <button data-testid="ticket-reply-send" disabled={busy || !reply.trim() || !a.conversation_id} onClick={() => sendReply(a.id, reply, () => setReply(""))}
                            className="rounded-full bg-redline-500 text-white text-sm font-semibold px-5 py-2 hover:bg-redline-600 disabled:opacity-40 flex items-center gap-1.5"><Send className="h-4 w-4" /> Send text</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Comments — the request conversation. Internal = staff-only; public replies
                    reach the resident in their portal (+ email/SMS per their prefs). */}
                <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="ticket-comments">
                  <Label icon={MessageSquareText}>Comments</Label>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {(detail.comments || []).length === 0 && <p className="text-xs text-ink/50">{noResident ? "No notes yet. Leave a note for your team." : `No comments yet. Reply to the ${V.lc("person")} or leave an internal note for your team.`}</p>}
                    {(detail.comments || []).map((c) => {
                      const isResident = c.author_type === "resident";
                      const isPublic = c.visibility === "public";
                      return (
                        <div key={c.id} data-testid={`ticket-comment-${c.id}`}
                          className={`rounded-xl p-3 border ${isResident ? "bg-sky-50/70 border-sky-100" : isPublic ? "bg-emerald-50/70 border-emerald-100" : "bg-amber-50/70 border-amber-100"}`}>
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <div className="text-xs font-semibold text-ink/70">{isResident ? `${c.author_name || `${V.one("person")}`} (${V.lc("person")})` : c.author_name}</div>
                            <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 ${isResident ? "bg-sky-200 text-sky-800" : isPublic ? "bg-emerald-200 text-emerald-800" : "bg-amber-200 text-amber-800"}`}>
                              {isResident ? `From ${V.lc("person")}` : isPublic ? `${V.one("person")} sees this` : "Internal"}
                            </span>
                          </div>
                          <div className="text-sm whitespace-pre-wrap break-words">{c.text}</div>
                          <div className="text-[11px] text-ink/45 mt-0.5">{fmt(c.created_at)}</div>
                        </div>
                      );
                    })}
                    <div ref={commentsEndRef} />
                  </div>
                  {!isClosed ? (
                    <div className="mt-3">
                      <div className="inline-flex rounded-full border border-sand-200 p-0.5 mb-2 text-xs font-semibold">
                        {!noResident && (
                          <button data-testid="ticket-comment-vis-public" onClick={() => setCommentVis("public")}
                            className={`px-3 py-1 rounded-full transition-colors ${commentVis === "public" ? "bg-emerald-500 text-white" : "text-ink/60"}`}>Public reply (portal)</button>
                        )}
                        <button data-testid="ticket-comment-vis-internal" onClick={() => setCommentVis("internal")}
                          className={`px-3 py-1 rounded-full transition-colors ${commentVis === "internal" ? "bg-amber-500 text-white" : "text-ink/60"}`}>Internal note</button>
                      </div>
                      <div className="flex gap-2 items-end">
                        <textarea data-testid="ticket-comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={2}
                          placeholder={commentVis === "public" ? `Write a reply the ${V.lc("person")} will see in their portal…` : "Leave a private note for your team…"}
                          className={`flex-1 rounded-xl border px-4 py-2.5 text-sm outline-none resize-y focus:ring-2 ${commentVis === "public" ? "border-emerald-200 focus:ring-emerald-500/30" : "border-amber-200 focus:ring-amber-500/30"}`} />
                        <button data-testid="ticket-comment-add" disabled={postingComment || !commentText.trim()} onClick={() => addComment(a.id)}
                          className={`rounded-xl text-white px-4 py-2.5 transition-colors disabled:opacity-50 ${commentVis === "public" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-amber-500 hover:bg-amber-600"}`}><Send className="h-4 w-4" /></button>
                      </div>
                      {commentVis === "public" && <p className="text-[11px] text-ink/45 mt-1.5">The {V.lc("person")} is notified in their portal{a.email ? ", by email" : ""}{a.phone ? " and by text (unless they've opted out)" : ""}.</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-ink/50 mt-3" data-testid="ticket-comments-closed-hint">Reopen the request to add a comment.</p>
                  )}
                </div>
                {/* activity trail — every status change, assignment and edit */}
                <div className="bg-white rounded-2xl border border-sand-200 p-5">
                  <Label icon={History}>Activity</Label>
                  <div className="max-h-72 overflow-y-auto" data-testid="ticket-activity">
                    {(detail.events || []).length === 0 && (
                      <p className="text-xs text-ink/50">No activity recorded yet — changes to this request will show up here from now on.</p>
                    )}
                    {(detail.events || []).slice().reverse().map((e) => {
                      const dot = { created: "bg-emerald-500", status: "bg-blue-500", assigned: "bg-violet-500",
                                    priority: "bg-redline-500", edited: "bg-sand-400", acknowledged: "bg-emerald-500",
                                    trashed: "bg-red-500", restored: "bg-emerald-500", due: "bg-amber-500",
                                    attachment: "bg-sky-500", taught: "bg-redline-500" }[e.kind] || "bg-sand-300";
                      return (
                        <div key={e.id} className="relative pl-5 pb-3 border-l border-sand-200 last:border-l-transparent ml-1.5" data-testid={`activity-event-${e.id}`}>
                          <span className={`absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full ${dot}`} />
                          <div className="text-sm text-ink"><span className="font-semibold">{e.who}</span> {e.detail}</div>
                          <div className="text-[11px] text-ink/45">{fmt(e.created_at)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* RIGHT — status + metadata sidebar (sticky) */}
              <div className="space-y-4 lg:sticky lg:top-6">
                <div className="bg-white rounded-2xl border border-sand-200 p-5 space-y-4" data-testid="ticket-status-card">
                  <div>
                    <Label icon={CheckCircle2}>Status</Label>
                    <select data-testid="ticket-status-select" disabled={readOnly} value={status} onChange={(e) => setStatus(a.id, e.target.value)}
                      className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60 font-semibold">
                      <option value="open">Open</option>
                      <option value="in_progress">In process</option>
                      <option value="on_hold">On hold</option>
                      <option value="resolved">Completed</option>
                      <option value="closed">Closed</option>
                    </select>
                    {!readOnly && !isClosed && status === "resolved" && (a.taught_knowledge_id
                      ? <span data-testid="ticket-taught-badge" className="mt-2 w-full rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold px-4 py-2 flex items-center justify-center gap-1.5"><Sparkles className="h-4 w-4" /> Added to knowledge</span>
                      : <button data-testid="ticket-teach" onClick={() => openTeach(a.id)} className="mt-2 w-full rounded-full bg-redline-500 text-white text-sm font-semibold px-4 py-2 hover:bg-redline-600 flex items-center justify-center gap-1.5"><Sparkles className="h-4 w-4" /> Teach the AI</button>)}
                    {!readOnly && isClosed && (
                      <button data-testid="ticket-reopen" onClick={() => reopenRequest(a.id)} className="mt-2 w-full rounded-full bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 flex items-center justify-center gap-1.5"><RotateCcw className="h-4 w-4" /> Reopen request</button>
                    )}
                  </div>
                </div>

                {tenant?.field_services_enabled
                  && (tenant?.entitlements?.field_services || tenant?.field_services_entitled) && (
                  <JobCard tenantId={tenantId} action={a} readOnly={readOnly}
                    moduleLabel={(tenant?.field_services_label || "").trim() || "Field services"}
                    onSent={() => refreshDetail(a.id)} />
                )}

                <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="ticket-submitted-by">
                  <Label icon={UserCircle2}>Submitted by</Label>
                  <div className="font-semibold text-ink flex items-center gap-2 flex-wrap">
                    {a.resident || (a.source === "board" ? a.raised_by_name || `${V.one("board")}` : `Unknown ${V.lc("person")}`)}
                    {a.person_id && a.is_owner != null && (
                      <span data-testid="ticket-owner-badge" className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide ${a.is_owner ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-sky-50 text-sky-700 border border-sky-200"}`}>
                        {a.is_owner ? "Owner" : "Tenant"}
                      </span>
                    )}
                  </div>
                  {(homeLabel(a) || a.household_id) && <div className="text-sm text-ink/70 mt-1 flex items-center gap-1.5"><Home className="h-3.5 w-3.5" /> {homeLabel(a) || "—"}</div>}
                  {a.source === "board" && <div className="text-sm text-ink/70 mt-1" data-testid="ticket-board-note">Raised by the {V.grp()} for the management team</div>}
                  {a.address && <div className="text-sm text-ink/70 mt-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {a.address}</div>}
                  {a.account_number && <div data-testid="ticket-account-number" className="text-sm text-ink/70 mt-1 flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> <span className="font-mono text-xs">{a.account_number}</span></div>}
                  {a.phone && <div className="text-sm text-ink/70 mt-1 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {a.phone}</div>}
                  {a.email && <div data-testid="ticket-email" className="text-sm text-ink/70 mt-1 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> <a href={`mailto:${a.email}`} className="hover:underline truncate">{a.email}</a></div>}
                </div>

                <div className="bg-white rounded-2xl border border-sand-200 p-5 space-y-4">
                  <div>
                    <Label icon={AlertTriangle}>Priority</Label>
                    <select data-testid="ticket-priority" disabled={readOnly} value={prioOf(a)} onChange={(e) => saveField(a.id, "priority", e.target.value)}
                      className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                      {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label icon={Tag}>Category</Label>
                    <select data-testid="ticket-category" disabled={readOnly} value={a.category || a.type || "general"} onChange={(e) => saveField(a.id, "category", e.target.value)}
                      className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                      {/* system-generated categories (e.g. new_resident) aren't in the manual list — keep the current value selectable */}
                      {!allCategories.some(([v]) => v === (a.category || a.type)) && (a.category || a.type) && (
                        <option value={a.category || a.type}>{catLabel(a.category || a.type)}</option>
                      )}
                      {allCategories.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label icon={UserCircle2}>Assigned to</Label>
                    <select data-testid="ticket-assignee" disabled={readOnly} value={a.assignee_id || ""} onChange={(e) => assign(a.id, admins.find((u) => u.user_id === e.target.value) || null)}
                      className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60">
                      <option value="">Unassigned</option>
                      {admins.map((u) => <option key={u.user_id} value={u.user_id}>{`${u.name} · ${u.role.replace("_", " ")}`}</option>)}
                    </select>
                  </div>
                  {!DONE(status) && (
                    <div>
                      <Label icon={CalendarClock}>Due date</Label>
                      <input type="date" data-testid="ticket-due-date" disabled={readOnly} defaultValue={a.due_at ? a.due_at.slice(0, 10) : ""} onChange={(e) => setDue(a.id, e.target.value)}
                        className="w-full rounded-lg border border-sand-200 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60 cursor-pointer" />
                    </div>
                  )}
                </div>

                {((readOnly && a.source === "board" && a.raised_by_user_id === me) || (!readOnly && noResident)) && (
                  <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="board-upload-card">
                    <Label icon={Paperclip}>Add a photo or file</Label>
                    <input ref={upFileRef} type="file" data-testid="board-upload-input" className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic"
                      onChange={(e) => onUpload(a.id, e.target.files?.[0])} />
                    <button data-testid="board-upload-btn" disabled={busy} onClick={() => upFileRef.current?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-sand-300 px-3 py-2.5 text-sm font-semibold text-ink/60 hover:border-ink/40 disabled:opacity-50 transition-colors">
                      <Paperclip className="h-4 w-4" /> Choose a file
                    </button>
                    <p className="text-xs text-ink/50 mt-2">Photos show above with the description; documents are filed below. Up to 5 MB each.</p>
                  </div>
                )}

                {(a.attachments || []).length > 0 && (
                  <div className="bg-white rounded-2xl border border-sand-200 p-5">
                    <Label icon={Paperclip}>Attachments</Label>
                    <div className="flex flex-col gap-1.5">
                      {a.attachments.map((f, i) => (
                        <a key={i} href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-redline-600 hover:underline">
                          <Download className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{f.filename}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {!readOnly && (
                  <div className="space-y-2">
                    {!isClosed && (
                      <button data-testid="ticket-close" onClick={() => closeRequest(a.id)}
                        title="Junk, duplicate or no action needed — stays on record under the Closed filter"
                        className="w-full rounded-full bg-white border border-sand-200 text-ink/60 text-sm font-semibold px-4 py-2 hover:bg-sand-50 hover:border-ink/30 hover:text-ink flex items-center justify-center gap-1.5">
                        <X className="h-4 w-4" /> Close request
                      </button>
                    )}
                    {a.archived ? (
                      <button data-testid="ticket-unarchive" onClick={() => archiveIds([a.id], false)}
                        className="w-full rounded-full bg-white border border-sand-200 text-ink/60 text-sm font-semibold px-4 py-2 hover:bg-sand-50 hover:border-ink/30 hover:text-ink flex items-center justify-center gap-1.5">
                        <ArchiveRestore className="h-4 w-4" /> Restore from archive
                      </button>
                    ) : (
                      <button data-testid="ticket-archive" onClick={() => archiveIds([a.id], true)}
                        title="Put it away — out of every list and count, still on record under the Archived filter"
                        className="w-full rounded-full bg-white border border-sand-200 text-ink/60 text-sm font-semibold px-4 py-2 hover:bg-sand-50 hover:border-ink/30 hover:text-ink flex items-center justify-center gap-1.5">
                        <Archive className="h-4 w-4" /> Archive
                      </button>
                    )}
                    {canDelete && (
                      <button data-testid="ticket-delete" onClick={() => deleteIds([a.id])}
                        title="Delete permanently — gone from the database after a short undo window"
                        className="w-full rounded-full bg-white border border-red-200 text-red-600 text-sm font-semibold px-4 py-2 hover:bg-red-50 hover:border-red-400 flex items-center justify-center gap-1.5">
                        <Trash2 className="h-4 w-4" /> Delete permanently
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      ) : (
      <>
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <PageTitle title={<><ClipboardList className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {(tenant?.requests_label || "").trim() || v.mod("requests")}</>} about={<>Organize and respond to {v.lcs("request")} from {v.lcs("people")}.</>} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="req-manage-btn" onClick={() => setManageOpen(true)} title="Categories & reports"
            className="rounded-full bg-white border border-sand-200 p-2.5 hover:border-ink transition-colors"><Settings2 className="h-4 w-4 text-ink/70" /></button>
          {readOnly && (
            <button data-testid="board-request-btn" onClick={openBoardRequest} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors">
              <Plus className="h-4 w-4" strokeWidth={2.5} /> Request to manager
            </button>
          )}
          {!readOnly && (
            <button data-testid="new-request-btn" onClick={openNew} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors">
              <Plus className="h-4 w-4" strokeWidth={2.5} /> Submit request
            </button>
          )}
        </div>
      </div>

      {/* filter bar */}
      <div className="flex gap-2 flex-wrap items-center mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="h-4 w-4 text-ink/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="req-search" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder={`Keyword, ${V.lc("person")} or unit`}
            className="w-full rounded-full border border-sand-200 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-redline-300" />
        </div>
        <select data-testid="req-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-full border border-sand-200 bg-white px-3.5 py-2.5 text-sm font-medium text-ink/80 outline-none focus:border-redline-300 cursor-pointer">
          <option value="">All statuses</option><option value="open">Open</option><option value="in_progress">In process</option><option value="on_hold">On hold</option><option value="resolved">Completed</option><option value="closed">Closed</option><option value="archived">Archived</option>
        </select>
        <select data-testid="req-category-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-full border border-sand-200 bg-white px-3.5 py-2.5 text-sm font-medium text-ink/80 outline-none focus:border-redline-300 cursor-pointer">
          <option value="">All categories</option>{allCategories.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {role !== "super_admin" && (
          <button data-testid="req-filter-mine" onClick={() => setMineOnly((v) => !v)}
            className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-1.5 ${mineOnly ? "bg-ink text-white" : "bg-white border border-sand-200 hover:border-ink text-ink/70"}`}>
            <UserCircle2 className="h-3.5 w-3.5" /> Assigned to me
          </button>
        )}
        <button data-testid="req-filter-board" onClick={() => setSourceFilter((v) => (v === "board" ? "" : "board"))}
          className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-1.5 ${sourceFilter === "board" ? "bg-indigo-600 text-white" : "bg-white border border-sand-200 hover:border-ink text-ink/70"}`}>
          <ClipboardList className="h-3.5 w-3.5" /> {V.Grp()} requests
        </button>
      </div>

      {/* count + table settings */}
      <div className="flex items-center justify-between mb-2">
        <span data-testid="req-showing" className="text-sm font-semibold text-ink/55">
          {loading && items.length === 0 ? "Loading…" : `Showing ${items.length} of ${total} item${total === 1 ? "" : "s"}`}
        </span>
        <div className="relative">
          <button data-testid="req-table-settings-btn" onClick={(e) => { e.stopPropagation(); setSettingsOpen((o) => !o); }} title="Table settings"
            className="rounded-lg border border-sand-200 bg-white p-2 hover:border-ink transition-colors flex items-center gap-1.5 text-sm font-medium text-ink/70"><SlidersHorizontal className="h-4 w-4" /></button>
          {settingsOpen && <RequestsTableSettings columns={columns} setColumns={setColumns} colMeta={COL_META} onClose={() => setSettingsOpen(false)} />}
        </div>
      </div>

      {/* bulk actions */}
      {!readOnly && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 rounded-xl bg-ink text-white px-4 py-2.5 shadow-md">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} selected</span>
          {statusFilter === "archived" ? (
            <button data-testid="bulk-unarchive-btn" onClick={() => bulkArchive(false)}
              className="rounded-full bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors">
              <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
            </button>
          ) : (
            <>
              {statusFilter === "closed" ? (
                <button data-testid="bulk-reopen-btn" onClick={() => bulkSetStatus("open")}
                  className="rounded-full bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors">
                  <RotateCcw className="h-3.5 w-3.5" /> Reopen
                </button>
              ) : (
                <button data-testid="bulk-close-btn" onClick={() => bulkSetStatus("closed")}
                  title="Junk, duplicate or no action needed — stays on record under the Closed filter"
                  className="rounded-full bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors">
                  <X className="h-3.5 w-3.5" /> Close
                </button>
              )}
              <button data-testid="bulk-archive-btn" onClick={() => bulkArchive(true)}
                title="Put these away — out of every list and count, still on record under the Archived filter"
                className="rounded-full bg-white/10 hover:bg-white/20 px-3.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors">
                <Archive className="h-3.5 w-3.5" /> Archive
              </button>
            </>
          )}
          {canDelete && (
            <button data-testid="bulk-delete-btn" onClick={bulkDelete}
              title="Delete permanently — gone from the database after a short undo window"
              className="rounded-full bg-red-500/90 hover:bg-red-500 px-3.5 py-1.5 text-sm font-semibold flex items-center gap-1.5 transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
          <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} className="text-sm text-white/70 hover:text-white ml-auto transition-colors">Clear</button>
        </div>
      )}

      {/* table — desktop/tablet. A 1071px-wide table on a phone means you see the title and
          have to swipe sideways for everything else, so mobile gets a card list instead. */}
      <div className="hidden sm:block bg-white rounded-2xl border overflow-x-auto border-sand-200">
        <table className="w-full text-sm" data-testid="req-table">
          <thead className="bg-sand-50 border-b border-sand-200">
            <tr>
              {!readOnly && (
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" data-testid="req-select-all" checked={allSelected} onChange={toggleAll}
                    className="h-4 w-4 accent-redline-500 cursor-pointer align-middle" title="Select all on this page" />
                </th>
              )}
              {visibleCols.map((c) => {
                const meta = COL_META[c.key]; const active = meta.sort && sortField === meta.sort;
                return (
                  <th key={c.key} data-testid={`req-col-${c.key}`} onClick={() => toggleSort(meta.sort)}
                    className={`text-left px-4 py-3 text-[11px] uppercase tracking-wide font-bold text-ink/50 whitespace-nowrap ${meta.sort ? "cursor-pointer hover:text-ink select-none" : ""}`}>
                    <span className="inline-flex items-center gap-1">{meta.label}
                      {active && (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={visibleCols.length + (readOnly ? 0 : 1)} className="px-4 py-16 text-center text-ink/40"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={visibleCols.length + (readOnly ? 0 : 1)} className="px-4 py-16 text-center">
                <div className="font-heading text-lg font-bold text-ink">No requests found</div>
                <div className="text-sm text-ink/50 mt-1">Try clearing filters, or new requests will appear here automatically.</div>
              </td></tr>
            ) : (
              items.map((x) => (
                <tr key={x.id} data-testid={`req-row-${x.id}`} onClick={() => openTicket(x)}
                  className={`border-b border-sand-100 last:border-0 hover:bg-sand-50 cursor-pointer transition-colors ${selected.has(x.id) ? "bg-redline-50/40" : ""}`}>
                  {!readOnly && (
                    <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`req-select-${x.id}`} checked={selected.has(x.id)} onChange={() => toggleSel(x.id)}
                        className="h-4 w-4 accent-redline-500 cursor-pointer align-middle" />
                    </td>
                  )}
                  {visibleCols.map((c) => <td key={c.key} className="px-4 py-3 max-w-[280px]">{renderCell(c.key, x)}</td>)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* mobile card list */}
      <div className="sm:hidden space-y-2" data-testid="req-mobile-list">
        {loading && items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-sand-200 px-4 py-16 text-center text-ink/40"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-sand-200 px-4 py-12 text-center">
            <div className="font-heading text-lg font-bold text-ink">No requests found</div>
            <div className="text-sm text-ink/50 mt-1">Try clearing filters, or new requests will appear here automatically.</div>
          </div>
        ) : items.map((x) => {
          const st = norm(x.status); const sm = STATUS_META[st]; const p = prioOf(x); const sla = slaState(x);
          return (
            <div key={x.id} data-testid={`req-card-${x.id}`} onClick={() => openTicket(x)}
              className={`bg-white rounded-2xl border px-3.5 py-3 flex items-start gap-3 active:bg-sand-50 transition-colors ${selected.has(x.id) ? "border-redline-300 bg-redline-50/40" : "border-sand-200"}`}>
              {!readOnly && (
                <input type="checkbox" data-testid={`req-card-select-${x.id}`} checked={selected.has(x.id)}
                  onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(x.id)}
                  className="h-4 w-4 mt-1 accent-redline-500 cursor-pointer shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {x.ref && <span className="text-[10px] font-mono font-bold text-redline-600 bg-redline-50 rounded px-1.5 py-0.5">{x.ref}</span>}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${sm.cls}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${sm.dot}`} /> {sm.label}
                  </span>
                  {x.archived && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sand-200 text-ink/60">Archived</span>}
                  {(p === "urgent" || p === "high") && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${PRIORITY_META[p].cls}`}>{PRIORITY_META[p].label}</span>}
                  {sla && sla.kind !== "ok" && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${sla.cls}`}>{sla.label}</span>}
                </div>
                <div className="font-heading font-bold text-ink text-[15px] mt-1 break-words">{x.title || x.summary || "Request"}</div>
                <div className="text-xs text-ink/55 mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-ink/70">{x.resident || "Unknown"}</span>
                  {homeLabel(x) && <span>· {homeLabel(x)}</span>}
                  <span className="text-ink/30">·</span>
                  <span>{fmt(x.created_at)}</span>
                </div>
                <div className="text-xs text-ink/50 mt-1 inline-flex items-center gap-1">
                  <UserCircle2 className="h-3.5 w-3.5 shrink-0" /> {x.assignee_name || "Unassigned"}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-ink/25 shrink-0 mt-1" />
            </div>
          );
        })}
      </div>

      {/* pagination */}
      <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-ink/55">
          <span>Rows per page</span>
          <select data-testid="req-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
            className="rounded-lg border border-sand-200 bg-white px-2.5 py-1.5 text-sm font-semibold outline-none focus:border-redline-300 cursor-pointer">
            <option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-ink/60">Page {page} of {totalPages}</span>
          <button data-testid="req-page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-lg border border-sand-200 bg-white p-2 hover:border-ink disabled:opacity-40 disabled:hover:border-sand-200"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="req-page-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-lg border border-sand-200 bg-white p-2 hover:border-ink disabled:opacity-40 disabled:hover:border-sand-200"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <RequestsManageModal open={manageOpen} onOpenChange={setManageOpen} tenantId={tenantId} tenantName={tenant?.name}
        builtIns={CATEGORIES} customCats={customCats} onCatsChanged={loadCats} readOnly={readOnly} />

      </>
      )}

      {/* New request dialog */}
      <Dialog open={newOpen} onOpenChange={(o) => { if (!o) setNewOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="new-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ClipboardList className="h-5 w-5 text-redline-500" /> {nf?.board ? "Request to manager" : "New request"}</DialogTitle>
          </DialogHeader>
          {nf && (
            <div className="space-y-4">
              {nf.board && (
                <>
                  <p className="text-xs text-ink/60 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">This goes to the manager you choose as {V.aGrp()} request — separate from {V.lc("person")} maintenance. Only they are alerted, and you'll be notified when it's picked up, handed on, or finished.</p>
                  <div>
                    <label className="text-sm font-semibold">Send to <span className="text-redline-500">*</span></label>
                    <select data-testid="nr-assignee" value={nf.assignee_id || ""} onChange={(e) => setNf({ ...nf, assignee_id: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                      <option value="">Choose a manager…</option>
                      {managers.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}{m.is_primary ? " · primary" : ""}</option>)}
                    </select>
                    <p className="mt-1 text-xs text-ink/40">
                      {managers.length === 0
                        ? `No manager is set up for this ${V.lc("org")} yet — ask Redline to add one.`
                        : "Only this manager is alerted. If they hand it on, you'll be told who has it."}
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-semibold">Title <span className="text-redline-500">*</span></label>
                <input data-testid="nr-title" value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })}
                  placeholder="e.g. Broken sprinkler at the pool gate" className="mt-1 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                {!nf.title.trim() && <p className="text-xs text-ink/40 mt-1">Required — this is what the team sees in the list.</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold">Category</label>
                  <select data-testid="nr-category" value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                    {allCategories.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold">Due date <span className="font-normal text-ink/40">(optional)</span></label>
                  <input type="date" data-testid="nr-due" value={nf.due_at ? nf.due_at.slice(0, 10) : ""}
                    onChange={(e) => setNf({ ...nf, due_at: e.target.value ? new Date(e.target.value + "T17:00:00").toISOString() : "" })}
                    className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30" />
                </div>
                <div>
                  <label className="text-sm font-semibold">Priority</label>
                  <select data-testid="nr-priority" value={nf.priority} onChange={(e) => setNf({ ...nf, priority: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                    {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              {!nf.board && (
              <div>
                <label className="text-sm font-semibold">{V.one("person")} <span className="font-normal text-ink/40">(optional — links their text thread so you can reply)</span></label>
                <select data-testid="nr-resident" value={nf.person_id}
                  onChange={(e) => { const opt = residentOptions.find((o) => o.pid === e.target.value); setNf({ ...nf, person_id: e.target.value, household_id: opt?.hid || "", resident: opt?.name || "" }); }}
                  className="mt-1 w-full rounded-xl border border-sand-200 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-redline-500/30">
                  <option value="">No {V.lc("person")} (internal request)</option>
                  {residentOptions.map((o) => <option key={o.pid} value={o.pid}>{o.label}</option>)}
                </select>
              </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">Details</label>
                  <TextAssist value={nf.details} onChange={(next) => setNf((f) => ({ ...f, details: next }))}
                    surface="request" testId="new-request-assist" micTitle="Dictate details" />
                </div>
                <textarea data-testid="nr-details" value={nf.details} onChange={(e) => setNf({ ...nf, details: e.target.value })} rows={3}
                  placeholder="What happened or what needs doing…" className="mt-1 w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
              </div>
              <div>
                <label className="text-sm font-semibold">Photos & files <span className="font-normal text-ink/40">(optional — up to 5 MB each)</span></label>
                <input ref={nfFileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.heic" data-testid="nr-files" className="hidden"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []);
                    const ok = picked.filter((f) => f.size > 0 && f.size <= 5 * 1024 * 1024);
                    picked.filter((f) => !ok.includes(f)).forEach((f) => toast.error(
                      f.size > 5 * 1024 * 1024 ? `${f.name} is over 5 MB` : `${f.name} is empty`));
                    setNfFiles((p) => [...p, ...ok]);
                    if (nfFileRef.current) nfFileRef.current.value = "";
                  }} />
                <button type="button" data-testid="nr-attach-btn" onClick={() => nfFileRef.current?.click()}
                  className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-sand-300 px-3 py-2.5 text-sm font-semibold text-ink/60 hover:border-ink/40 transition-colors">
                  <Paperclip className="h-4 w-4" /> Add photos or documents
                </button>
                {nfFiles.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="nr-file-list">
                    {nfFiles.map((f, i) => (
                      <div key={i} data-testid={`nr-file-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-sand-50 px-3 py-1.5 text-xs">
                        <span className="truncate text-ink/70 inline-flex items-center gap-1.5"><Paperclip className="h-3 w-3 shrink-0" /> {f.name}</span>
                        <button type="button" data-testid={`nr-file-remove-${i}`} onClick={() => setNfFiles((p) => p.filter((_, j) => j !== i))} className="text-ink/40 hover:text-red-600 shrink-0"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setNewOpen(false)} className="rounded-full px-5 py-2.5 font-semibold text-ink/70 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="nr-create" disabled={busy} onClick={createRequest} className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 disabled:opacity-50 flex items-center gap-1.5 transition-colors"><ClipboardList className="h-4 w-4" /> {nf?.board ? "Send request" : "Create request"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!teach} onOpenChange={(o) => { if (!o) setTeach(null); }}>
        <DialogContent className="max-w-lg" data-testid="teach-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-5 w-5 text-redline-500" /> Teach the AI from this ticket</DialogTitle>
          </DialogHeader>
          {teach?.loading ? (
            <div className="py-12 flex flex-col items-center gap-3 text-ink/60" data-testid="teach-loading">
              <Loader2 className="h-7 w-7 animate-spin text-redline-500" />
              <span className="text-sm">Drafting a reusable answer from this conversation…</span>
            </div>
          ) : teach?.empty ? (
            <div className="py-8 text-center text-sm text-ink/60" data-testid="teach-empty">
              There isn't a clear, reusable lesson in this ticket. You can still write one below if you'd like.
              <div className="mt-4 space-y-2 text-left">
                <input value={teach.title} onChange={(e) => setTeach({ ...teach, title: e.target.value })} placeholder={`Question ${V.lcs("person")} might ask`}
                  className="w-full rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-sm font-semibold outline-none focus:border-ink" data-testid="teach-title" />
                <div className="relative">
                  <textarea value={teach.answer} onChange={(e) => setTeach({ ...teach, answer: e.target.value })} rows={4} placeholder="The answer the AI should give"
                    className="w-full rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 pr-10 text-sm outline-none focus:border-ink" data-testid="teach-answer" />
                  <span className="absolute top-2 right-2"><TextAssist value={teach.answer} onChange={(next) => setTeach((tc) => ({ ...tc, answer: next }))} surface="knowledge" testId="teach-empty-assist" micTitle="Dictate answer" /></span>
                </div>
              </div>
            </div>
          ) : teach ? (
            <div className="space-y-3">
              <p className="text-sm text-ink/60">Review the AI's draft below. It'll be added to your knowledge base so {V.lcs("person")} get this answer automatically next time — edit anything before saving.</p>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Question</label>
                <input value={teach.title} onChange={(e) => setTeach({ ...teach, title: e.target.value })}
                  className="w-full mt-1 rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-sm font-semibold outline-none focus:border-ink" data-testid="teach-title" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Answer</label>
                  <TextAssist value={teach.answer} onChange={(next) => setTeach((tc) => ({ ...tc, answer: next }))}
                    surface="knowledge" testId="teach-answer-assist" micTitle="Dictate answer" />
                </div>
                <textarea value={teach.answer} onChange={(e) => setTeach({ ...teach, answer: e.target.value })} rows={5}
                  className="w-full mt-1 rounded-lg bg-sand-50 border border-sand-200 px-3 py-2 text-sm outline-none focus:border-ink" data-testid="teach-answer" />
              </div>
            </div>
          ) : null}
          {teach && !teach.loading && (
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setTeach(null)} className="rounded-full bg-sand-100 text-ink font-semibold px-4 py-2 text-sm hover:bg-sand-200" data-testid="teach-cancel">Cancel</button>
              <button onClick={saveTeach} disabled={busy} className="rounded-full bg-ink text-white font-semibold px-5 py-2 text-sm hover:bg-ink-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="teach-save"><CheckCircle2 className="h-4 w-4" /> Add to knowledge</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div data-testid="lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out">
          <button data-testid="lightbox-close" className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 text-xl leading-none">×</button>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

